import { FormValidator } from 'apps/webcomponents/src/ui/gzForm/formField/types/form-validator';
import { LitElement, html, TemplateResult } from 'lit';
import { property, customElement, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { GzTextareaWebComponent } from 'apps/webcomponents/src/ui/gzInput/component/GzTextareaWebComponent';
import { GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { Subscription } from 'rxjs';
import { FontSizes } from '../../../types/common/types';
import { translateFn } from '../../../utils/translator.helper';
import { ButtonColors, ButtonTypes } from '../../../ui/gzButton/types';
import { debounce } from '../../../utils/event.utils';
import { RequiredValidator } from '../../../ui/gzForm/validators/required.validator';
import { FormBuildConfig } from '../../../ui/gzForm/formGroup/types/form-build-config';
import { GzDialogEvents } from '../../../ui/gzDialog/component/GzDialog';

/**
 * @summary feedback content for dialog
 * @tag gz-ai-feedback
 */

@customElement('gz-ai-feedback')
export class GzAiFeedbackWebComponent extends LitElement {
  @property({
    attribute: false,
    reflect: false,
    type: Array,
  })
  public validators: Array<FormValidator> = [];

  /**
   * @internal
   * @type {FormGroup}
   * @private
   */
  private formGroup: FormGroup;

  /**
   * @internal
   * @type {GzTextareaWebComponent}
   * @private
   */
  @query('#textareaInput')
  private inputComponent!: GzTextareaWebComponent;

  /**
   * @internal
   * @type {GzFormFieldWebComponent}
   * @private
   */
  @query('#feedBackField')
  private feedBackField!: GzFormFieldWebComponent;

  /**
   * @internal
   * @type {GzFormFieldWebComponent}
   * @private
   */
  @query('#ratingField')
  private ratingField!: GzFormFieldWebComponent;

  /**
   * @internal
   * @type {GzButtonWebComponent}
   * @private
   */
  @query('#submitButton')
  private submitBtn: GzButtonWebComponent;

  /**
   * @internal
   * @type {Subscription | null}
   * @private
   */
  @state()
  private subscription: Subscription | null;

  static get styles() {
    return styles;
  }

  render(): TemplateResult {
    return html`
      <div class="feedback-form-content">
        <gz-text fontSize="${FontSizes.HEADING_6}" class="mb-1">
          ${translateFn('Your feedback helps us improve the AI Assistant.')}
        </gz-text>
        <gz-form-field label="${translateFn('Comments')}" id="feedBackField" class="pt-2">
          <gz-textarea
            maxLength="64"
            gz-form-input
            type="text"
            id="textareaInput"
            hasCounter="true"
            maxChars="500"
            resizablebycontent
            placeholder="${translateFn('Type here')}"
          ></gz-textarea>
          <gz-input-error id="inputError" class="pb-1"></gz-input-error>
        </gz-form-field>
        <gz-form-field label="${translateFn('Rating')}" id="ratingField" class="pb-4">
          <gz-star-rating gz-form-input></gz-star-rating>
        </gz-form-field>
      </div>
      <div class="d-flex justify-space-between item-align-center">
        <gz-button type="${ButtonTypes.FULL}" id="submitButton" class="mr-2" @click="${this.handleSubmitButton}">
          ${translateFn('Submit')}
        </gz-button>
        <gz-button type="${ButtonTypes.OUTLINE}" color="${ButtonColors.SECONDARY}" @click="${this.handleCancelButton}">
          ${translateFn('Cancel')}
        </gz-button>
      </div>
    `;
  }

  disconnectedCallback() {
    this.subscription?.unsubscribe();
  }

  public firstUpdated(): void {
    this.setValidators();
    this.buildConfig();
    this.addSubscription();

    debounce((): void => {
      this.inputComponent.inputFocus();
    }, 100);
  }

  private setValidators(): void {
    this.feedBackField.Validators = [RequiredValidator(translateFn('This field is required.'))];
  }

  private buildConfig(): void {
    const config: FormBuildConfig = {
      message: this.feedBackField,
      rating: this.ratingField,
    };

    this.formGroup = FormGroup.build(config);
  }

  private addSubscription(): void {
    this.subscription = this.formGroup.valid$.subscribe({
      next: valid => {
        this.submitBtn.disabled = !valid;
      },
    });
  }

  private handleCancelButton(): void {
    this.dispatchEvent(
      new CustomEvent(GzDialogEvents.CANCEL, {
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleSubmitButton(): void {
    this.submitBtn.showSpinner = true;
    this.dispatchEvent(
      new CustomEvent(GzDialogEvents.CONFIRM, {
        bubbles: true,
        composed: true,
        detail: {
          message: this.formGroup.value.message,
          rating: this.formGroup.value.rating,
        },
      })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-ai-feedback': GzAiFeedbackWebComponent;
  }
}
